// src/pages/CreateAccount.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Crown, Sparkles, Eye, EyeOff, CheckCircle, XCircle, UserRoundPen, ArrowLeft, Shield, Users, User, Plus, X, Check } from 'lucide-react';
import Footer from '../components/Footer';
import { COURSE_PACKAGES, getSortedPackages } from '../config/coursePackages';
import { ACCOUNT_CONFIG } from '../config/accountConfig';
import ProfileContactFields from '../components/ProfileContactFields';
import { validateProfileFields } from '../config/locationData';
import { validatePassword } from '../utils/passwordValidation';

function CreateAccount() {
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Account creation form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Validation states
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  
  // Policy agreement states
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  
  // Save payment method checkbox (moved from SelectPlan)
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);

  // Account type toggle: 'family' (default) or 'adult'
  const [isAdult, setIsAdult] = useState(false);

  // Children state - each child has: name, dateOfBirth, packageId
  const [children, setChildren] = useState([
    { name: '', dateOfBirth: null, packageId: null }
  ]);

  // Parent contact info (phone, country, state/province, city)
  const [contactInfo, setContactInfo] = useState({
    phoneNumber: '',
    country: '',
    stateProvince: '',
    city: ''
  });
  const [contactErrors, setContactErrors] = useState({});

  // Redirect authenticated users
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/home', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // In adult mode, keep the learner name in sync with the account-holder name fields
  useEffect(() => {
    if (!isAdult) return;
    const fullName = `${firstName} ${lastName}`.trim();
    setChildren(prev => {
      const next = [...prev];
      next[0] = { ...next[0], name: fullName };
      return next;
    });
  }, [isAdult, firstName, lastName]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };

  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return isAdult ? 'Please enter your date of birth' : 'Please select date of birth';

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (isAdult) {
      if (age < 18) return 'You must be at least 18 years old to enroll as an adult learner';
    } else {
      if (age < 3) return 'Child must be at least 3 years old';
    }

    return '';
  };

  // Input handlers with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
    
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(value !== password ? 'Passwords do not match' : '');
  };

  // Merge partial updates from ProfileContactFields into contactInfo
  const handleContactChange = (update) => {
    setContactInfo((prev) => ({ ...prev, ...update }));
  };

  // Children management functions
  const addChild = () => {
    if (children.length < 5) {
      setChildren([...children, { name: '', dateOfBirth: null, packageId: null }]);
    }
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      const updated = children.filter((_, i) => i !== index);
      setChildren(updated);
    }
  };

  const updateChildName = (index, name) => {
    const updated = [...children];
    updated[index] = { ...updated[index], name };
    setChildren(updated);
  };

  const updateChildDOB = (index, dateOfBirth) => {
    const updated = [...children];
    updated[index] = { ...updated[index], dateOfBirth };
    setChildren(updated);
  };

  const updateChildPackage = (index, packageId) => {
    const updated = [...children];
    updated[index] = { ...updated[index], packageId };
    setChildren(updated);
  };

  const handleAccountTypeToggle = (type) => {
    const adult = type === 'adult';
    setIsAdult(adult);
    if (adult) {
      const adultPkgs = getSortedPackages().filter(p => p.audience === 'adult');
      const autoId = adultPkgs.length === 1 ? adultPkgs[0].id : null;
      const fullName = `${firstName} ${lastName}`.trim();
      setChildren([{ name: fullName, dateOfBirth: null, packageId: autoId }]);
    } else {
      setChildren([{ name: '', dateOfBirth: null, packageId: null }]);
    }
  };

  // Validate all learners before submission
  const validateAllChildren = () => {
    for (let i = 0; i < children.length; i++) {
      if (!children[i].name || children[i].name.trim() === '') {
        if (isAdult) return { valid: false, message: 'Please enter your name' };
        return { valid: false, message: `Please enter a name for ${children.length > 1 ? `Child ${i + 1}` : 'your child'}` };
      }
    }

    for (let i = 0; i < children.length; i++) {
      if (!children[i].dateOfBirth) {
        if (isAdult) return { valid: false, message: 'Please enter your date of birth' };
        return { valid: false, message: `Please enter date of birth for ${children[i].name}` };
      }

      const ageValidation = validateAge(children[i].dateOfBirth);
      if (ageValidation) {
        if (isAdult) return { valid: false, message: ageValidation };
        return { valid: false, message: `${children[i].name}: ${ageValidation}` };
      }
    }

    for (let i = 0; i < children.length; i++) {
      if (!children[i].packageId) {
        if (isAdult) return { valid: false, message: 'Please select a plan' };
        return { valid: false, message: `Please select a plan for ${children[i].name}` };
      }
    }

    return { valid: true };
  };

  // Calculate running total
  const calculateTotal = () => {
    return children.reduce((total, child) => {
      if (child.packageId && COURSE_PACKAGES[child.packageId]) {
        return total + COURSE_PACKAGES[child.packageId].price;
      }
      return total;
    }, 0);
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
    
    if (score === 0) return { strength: 'Very Weak', color: 'bg-red-500', width: '0%' };
    if (score === 1) return { strength: 'Weak', color: 'bg-red-400', width: '20%' };
    if (score === 2) return { strength: 'Fair', color: 'bg-yellow-400', width: '40%' };
    if (score === 3) return { strength: 'Good', color: 'bg-yellow-500', width: '60%' };
    if (score === 4) return { strength: 'Strong', color: 'bg-green-400', width: '80%' };
    if (score === 5) return { strength: 'Very Strong', color: 'bg-green-500', width: '100%' };
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setIsCreatingAccount(true);
    setError(null);
    
    // Final validation check
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    const confirmPasswordValidation = password !== confirmPassword ? 'Passwords do not match' : '';
    const childrenValidation = validateAllChildren();
    const contactValidation = validateProfileFields(contactInfo);
    const hasContactErrors = Object.keys(contactValidation).length > 0;

    if (emailValidation || passwordValidation || confirmPasswordValidation || !childrenValidation.valid || hasContactErrors) {
      setEmailError(emailValidation);
      setPasswordError(passwordValidation);
      setConfirmPasswordError(confirmPasswordValidation);
      setContactErrors(contactValidation);
      if (!childrenValidation.valid) {
        setError(childrenValidation.message);
      } else if (hasContactErrors) {
        setError('Please complete your contact information.');
      }
      setIsCreatingAccount(false);
      return;
    }
    
    // Policy agreement validation
    if (!agreedToTerms || !agreedToPrivacy) {
      setError('Please agree to our Terms of Service and Privacy Policy to continue.');
      setIsCreatingAccount(false);
      return;
    }
    
    try {
      // 1. Create the user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      
      // 2. Set display name in Firebase Auth
      await updateProfile(newUser, {
        displayName: `${firstName} ${lastName}`
      });
      
      // 3. Store user data with children array structure
      const baseUserData = {
        firstName,
        lastName,
        email,
        phoneNumber: contactInfo.phoneNumber.trim(),
        country: contactInfo.country.trim(),
        stateProvince: contactInfo.stateProvince.trim(),
        city: contactInfo.city.trim(),
        accountType: isAdult ? 'adult' : 'family',
        createdAt: new Date().toISOString(),
        policyAgreements: {
          termsAccepted: true,
          privacyAccepted: true,
          acceptanceDate: new Date().toISOString()
        },
        payment: {
          stripeCustomerId: null,
          savedPaymentMethodId: null,
          savePaymentMethod: savePaymentMethod
        }
      };

      // Create children array with individual packageIds
      const userData = {
        ...baseUserData,
        children: children.map((child, index) => ({
          id: `child_${Date.now()}_${index}`,
          name: child.name,
          dateOfBirth: child.dateOfBirth.toISOString(),
          createdAt: new Date().toISOString(),
          order: index,
          course: {
            packageId: child.packageId, // Each child has their own packageId
            status: 'pending',
            createdAt: new Date().toISOString(),
            pendingExpiresAt: new Date(Date.now() + ACCOUNT_CONFIG.PENDING_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString(),
            startDate: null,
            endDate: null,
            earlyRenewalDate: null
          }
        }))
      };

      await setDoc(doc(db, "users", newUser.uid), userData);

      // 4. Store user data in localStorage for payment page
      localStorage.setItem("user", JSON.stringify({
        uid: newUser.uid,
        email: newUser.email,
        firstName,
        lastName,
        children: children.map((child, index) => ({
          name: child.name,
          dateOfBirth: child.dateOfBirth,
          packageId: child.packageId
        })),
        savePaymentMethod: savePaymentMethod
      }));
      
      // 5. Redirect to complete subscription
      navigate("/complete-subscription", { replace: true });
    } catch (err) {
      console.error('Account creation error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleBackToPlanSelection = () => {
    navigate('/select-plan');
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold">Loading...</div>
        </div>
      </div>
    );
  }

  const packages = COURSE_PACKAGES;
  const total = calculateTotal();
  const availablePackages = getSortedPackages().filter(pkg =>
    isAdult ? pkg.audience === 'adult' : pkg.audience === 'child'
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
            <UserRoundPen className="text-yellow-400" size={24} />
            <span className="text-white font-bold text-lg">Create Your Account</span>
            <UserRoundPen className="text-yellow-400" size={24} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 drop-shadow-2xl">
            Almost There!
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            {isAdult
              ? 'Create your account and select the perfect plan for yourself'
              : 'Create your account and select the perfect plan for each child'}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <button
            onClick={handleBackToPlanSelection}
            className="flex items-center gap-2 text-white hover:text-purple-200 mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Plans
          </button>

          {/* Main Form Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl mb-8">
            <form onSubmit={handleCreateAccount}>

              {/* Enrollment type toggle */}
              <div className="mb-8 p-5 bg-blue-50 rounded-2xl border border-blue-200">
                <div className="text-sm font-semibold text-gray-700 mb-3">Who are you enrolling?</div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleAccountTypeToggle('family')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold transition-all text-sm ${
                      !isAdult
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    My child(ren)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAccountTypeToggle('adult')}
                    className={`flex-1 py-3 px-4 rounded-xl border-2 font-semibold transition-all text-sm ${
                      isAdult
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    Myself (adult learner)
                  </button>
                </div>
              </div>

              {/* Parent Information Section */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Shield className="text-purple-600" size={24} />
                  {isAdult ? 'Your Information' : 'Parent Information'}
                </h3>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      emailError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {emailError && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <XCircle size={16} />
                      {emailError}
                    </div>
                  )}
                </div>

                {/* Password */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={handlePasswordChange}
                      className={`w-full px-4 py-3 pr-12 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        passwordError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Password strength</span>
                        <span className={`font-medium ${getPasswordStrength(password).color.replace('bg-', 'text-')}`}>
                          {getPasswordStrength(password).strength}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrength(password).color}`}
                          style={{ width: getPasswordStrength(password).width }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {passwordError && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <XCircle size={16} />
                      {passwordError}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      className={`w-full px-4 py-3 pr-12 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        confirmPasswordError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {confirmPassword && !confirmPasswordError && (
                    <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                      <CheckCircle size={16} />
                      Passwords match
                    </div>
                  )}
                  {confirmPasswordError && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <XCircle size={16} />
                      {confirmPasswordError}
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="mt-4">
                  <ProfileContactFields
                    values={contactInfo}
                    onChange={handleContactChange}
                    errors={contactErrors}
                  />
                </div>
              </div>

              {/* Children Information Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Sparkles className="text-purple-600" size={24} />
                    {isAdult ? 'Your Plan' : 'Children & Plans'}
                  </h3>
                  {!isAdult && children.length < 5 && (
                    <button
                      type="button"
                      onClick={addChild}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold rounded-xl transition-colors"
                    >
                      <Plus size={18} />
                      Add Another Child
                    </button>
                  )}
                </div>

                {/* Collapsible Compare Plans Section */}
                <details className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <summary className="cursor-pointer font-semibold text-purple-700 hover:text-purple-900 flex items-center gap-2">
                    <span>📊 Compare Plans - See Full Details</span>
                  </summary>
                  <div className="mt-4 flex flex-wrap justify-center gap-4">
                    {availablePackages.map(pkg => (
                      <div key={pkg.id} className="w-full md:w-[calc(50%-0.5rem)] p-4 bg-white rounded-xl border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          {pkg.type === 'group' ? (
                            <Users className="text-purple-600" size={20} />
                          ) : (
                            <User className="text-purple-600" size={20} />
                          )}
                          <h4 className="font-bold text-gray-800">{pkg.name}</h4>
                        </div>
                        <div className="text-2xl font-black text-purple-600 mb-3">${pkg.price} CAD</div>
                        <ul className="space-y-2 text-sm text-gray-700">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>

                {children.map((child, index) => (
                  <div key={index} className="mb-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-200 relative">
                    {/* Remove Button */}
                    {children.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChild(index)}
                        className="absolute top-4 right-4 p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                        title="Remove this child"
                      >
                        <X size={20} />
                      </button>
                    )}

                    {children.length > 1 && (
                      <h4 className="text-lg font-bold text-purple-700 mb-4">
                        Child {index + 1}
                      </h4>
                    )}
                    
                    {/* Learner's Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAdult ? 'Your Name' : "Child's Full Name"}
                      </label>
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => updateChildName(index, e.target.value)}
                        placeholder={isAdult ? 'Your full name' : "Enter child's full name"}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        required
                      />
                    </div>

                    {/* Date of Birth */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {isAdult ? 'Your Date of Birth' : 'Date of Birth'}
                      </label>
                      <DatePicker
                        selected={child.dateOfBirth}
                        onChange={(date) => updateChildDOB(index, date)}
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Select date of birth"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        maxDate={new Date()}
                        showYearDropdown
                        yearDropdownItemNumber={isAdult ? 80 : 20}
                        scrollableYearDropdown
                        required
                      />
                    </div>

                    {/* Mini Plan Cards */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Select Plan
                      </label>
                      <div className={`grid gap-4 ${availablePackages.length === 1 ? 'grid-cols-1 max-w-xs' : availablePackages.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3'}`}>
                        {availablePackages.map(pkg => (
                          <div
                            key={pkg.id}
                            onClick={() => updateChildPackage(index, pkg.id)}
                            className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${
                              child.packageId === pkg.id
                                ? 'border-purple-500 bg-purple-50 shadow-md scale-105'
                                : 'border-gray-300 bg-white hover:border-purple-300'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              {pkg.type === 'group' ? (
                                <Users className={`${child.packageId === pkg.id ? 'text-purple-600' : 'text-gray-400'}`} size={24} />
                              ) : (
                                <User className={`${child.packageId === pkg.id ? 'text-purple-600' : 'text-gray-400'}`} size={24} />
                              )}
                              {child.packageId === pkg.id && (
                                <div className="bg-green-500 text-white rounded-full p-1">
                                  <Check size={14} />
                                </div>
                              )}
                            </div>
                            <div className="font-bold text-gray-800 mb-1">{pkg.shortLabel}</div>
                            <div className="text-2xl font-black text-purple-600 mb-2">${pkg.price}</div>
                            <div className="text-xs text-gray-600 leading-relaxed">
                              {pkg.cardDescription}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Running Total */}
                {total > 0 && (
                  <div className="p-4 bg-purple-100 rounded-xl border-2 border-purple-300">
                    <div className="flex justify-between items-center">
                      <div className="font-semibold text-gray-700">
                        {isAdult
                          ? 'Total'
                          : `Total for ${children.length} ${children.length === 1 ? 'child' : 'children'}`}
                      </div>
                      <div className="text-2xl font-black text-purple-700">
                        ${total.toFixed(2)} CAD
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Policy Agreements */}
              <div className="mb-6 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <label htmlFor="terms" className="text-sm text-gray-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/terms-of-service', '_blank')}
                      className="text-purple-600 hover:text-purple-800 underline font-medium"
                    >
                      Terms of Service
                    </button>
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <label htmlFor="privacy" className="text-sm text-gray-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/privacy-policy', '_blank')}
                      className="text-purple-600 hover:text-purple-800 underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="text-red-700 text-sm font-medium">{error}</div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isCreatingAccount}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none shadow-lg hover:shadow-xl"
              >
                {isCreatingAccount ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating Account...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Crown size={20} />
                    Create Account & Continue to Payment
                  </div>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}

export default CreateAccount;